import { ArchiveIcon } from '@heroicons/react/outline';
import * as React from 'react';
import './Table.scss';
import { UseMutationResult } from 'react-query';
import { ReactComponent as NotFound } from '@/assets/img/not-found.svg';
import { ReactComponent as RefreshSmall } from '@/assets/img/refresh-small.svg';
import { ReactComponent as SortUp } from '@/assets/img/table-sort-up.svg';
import { ReactComponent as SortDown } from '@/assets/img/table-sort-down.svg';
import { Button } from '../Button';
import ReactPaginate from 'react-paginate';
type TableColumn<Entry> = {
  title: string;
  field: keyof Entry;
  width?: number;
  HeaderCell?(): React.ReactElement;
  Cell?({ entry }: { entry: Entry }): React.ReactElement;
  hide?: boolean;
  enableSort?: boolean;
};

export type TableProps<Entry> = {
  data?: Entry[];
  columns: TableColumn<Entry>[];
  tableInitialState: {
    tableTotalRecords: number;
    tablePageIndex: number;
  };
  isDataLoading?: boolean;
  showPagination?: boolean;
  onTableStateChange?: (pageIndex: number, reset: boolean) => any;
  onSortChange?: (field: keyof Entry | null, direction: 'asc' | 'desc' | null) => any;
  children?: React.ReactElement;
  rowClickable?: boolean;
  hideHeader?: boolean;
  onRowClick?: (entry: Entry) => void;
  increaseHeight?: boolean;
  blinkRow?: boolean;
};

export const TableData = <Entry extends { id?: string }>({
  data,
  columns,
  tableInitialState,
  isDataLoading,
  onTableStateChange,
  onSortChange,
  children,
  showPagination,
  rowClickable,
  onRowClick,
  hideHeader,
  increaseHeight,
  blinkRow = false,
}: TableProps<Entry>) => {
  const tableContainerRef = React.useRef<HTMLDivElement>(null);
  const [blinkFirstRow, setBlinkFirstRow] = React.useState(false);
  const [sortField, setSortField] = React.useState<keyof Entry | null>(null);
  const [sortDirection, setSortDirection] = React.useState<'asc' | 'desc' | null>(null);

  // Add effect to trigger blinking animation
  React.useEffect(() => {
    if (blinkRow && data && data.length > 0) {
      setBlinkFirstRow(true);
      const timer = setTimeout(() => {
        setBlinkFirstRow(false);
      }, 2500); // 5 blinks * 500ms per blink cycle
      return () => clearTimeout(timer);
    }
  }, [blinkRow, data]);

  const fetchMoreOnBottomReached = (containerRefElement?: HTMLDivElement | null) => {
    if (containerRefElement && !showPagination) {
      const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
      if (scrollHeight - scrollTop - clientHeight < 50 && !isDataLoading) {
        getNextSetOfData();
      }
    }
  };

  const getNextSetOfData = () => {
    const totalRecords = data && data.length ? data.length : 0;
    if (totalRecords < tableInitialState.tableTotalRecords) {
      onTableStateChange && onTableStateChange(tableInitialState.tablePageIndex + 1, false);
      //setSelectedPage(selectedPage + 1);
    }
  };
  const onTableRowClicked = (e: any, entry: Entry) => {
    rowClickable && onRowClick && onRowClick(entry);
  };
  const handlePageClick = (event: any) => {
    onTableStateChange && onTableStateChange(event.selected + 1, true);
  };

  const handleHeaderClick = (field: keyof Entry, enableSort: boolean) => {
    if (!enableSort) return;

    let newDirection: 'asc' | 'desc' | null = 'desc';
    if (sortField === field) {
      if (sortDirection === 'asc') {
        newDirection = null;
      } else if (sortDirection === 'desc') {
        newDirection = 'asc';
      } else {
        newDirection = 'asc';
      }
    }

    setSortField(newDirection ? field : null);
    setSortDirection(newDirection);
    onSortChange && onSortChange(newDirection ? field : null, newDirection);
  };

  if (!children && !data?.length && !isDataLoading) {
    return (
      <>
        {window.location.pathname == '/app/events' ? (
          <div className="not-found">
            <div className="n-found-img-outer">
              <NotFound />
            </div>

            <div className="title-text-not-found">You have no appointment yet</div>
            <div className="sub-title-text-not-found">
              Share your store link with customers for booking. If they
            </div>
            <div className="sub-title-text-not-found">
              already booked, click
              <RefreshSmall /> button at the top.
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center text-gray-500 bg-white h-80">
            <ArchiveIcon className="w-16 h-16" />
            <h4>
              {window.location.pathname == '/app/reviews' ? 'No Reviews' : 'No Entries Found'}
            </h4>
          </div>
        )}
      </>
    );
  }
  return (
    <div>
      {data?.length ? (
        <div
          className={`table-container ${showPagination ? 'pagination' : ''} ${increaseHeight ? 'increase-apt-height' : ''
            }`}
          onScroll={(e) => fetchMoreOnBottomReached(e.target as HTMLDivElement)}
          ref={tableContainerRef}
        >
          <table className="petsked-table min-w-full divide-y divide-gray-200">
            {!hideHeader && (
              <thead>
                <tr>
                  {columns.map(({ HeaderCell, title, width, hide, enableSort, field }, index) =>
                    hide ? null : (
                      <th
                        key={title + index}
                        scope="col"
                        title={`${enableSort ? 'Click to sort' : ''}`}
                        style={{ width: `${width}%`, maxWidth: `${width}%` }}
                        className={`px-6 py-3 text-lg font-medium tracking-wider text-left text-gray-500 uppercase ${enableSort ? 'cursor-pointer hover:bg-gray-100' : ''
                          }`}
                        onClick={() => handleHeaderClick(field, enableSort || false)}
                      >
                        <div className="d-flex items-center gap-2">
                          <span>
                            {HeaderCell ? (
                              <span className="d-flex items-center">
                                <HeaderCell />
                                {title}
                              </span>
                            ) : (
                              title
                            )}
                          </span>
                          {enableSort && (
                            <span className="sort-icon flex gap-0.5">
                              <span className={sortField === field && sortDirection === 'asc' ? 'opacity-100' : 'opacity-30'} style={{ display: 'flex', transform: 'scale(1.25)', transformOrigin: 'center' }}>
                                <SortUp />
                              </span>
                              <span className={sortField === field && sortDirection === 'desc' ? 'opacity-100' : 'opacity-30'} style={{ display: 'flex', transform: 'scale(1.25)', transformOrigin: 'center' }}>
                                <SortDown />
                              </span>
                            </span>
                          )}
                        </div>
                      </th>
                    )
                  )}
                </tr>
              </thead>
            )}
            <tbody>
              {data &&
                data.map((entry: any, entryIndex) => (
                  <>
                    <tr
                      key={entry?.id || entryIndex}
                      className={`odd:bg-white ${rowClickable ? 'clickable' : ''}${entry.isPrimary ? 'selected' : ''
                        } ${entryIndex === 0 && blinkFirstRow ? 'blink-row' : ''}`}
                      onClick={(e) => onTableRowClicked(e, entry)}
                    >
                      {columns.map(({ Cell, field, title, hide }, columnIndex) =>
                        hide ? null : (
                          <td
                            key={title + columnIndex}
                            className="px-6 py-2 text-lg font-medium text-gray-400 whitespace-nowrap"
                          >
                            {Cell ? <Cell entry={entry} /> : entry[field]}
                          </td>
                        )
                      )}
                    </tr>
                    {entry &&
                      !entry.hide &&
                      entry['childServices'] &&
                      entry['childServices'].map((entry: any, entryIndex: number) => (
                        <>
                          <tr
                            key={entry?.id || entryIndex}
                            className={`child-row odd:bg-white ${rowClickable ? 'clickable' : ''}`}
                            onClick={(e) => onTableRowClicked(e, entry)}
                          >
                            {columns.map(({ Cell, field, title, hide }, columnIndex) =>
                              hide ? null : (
                                <td
                                  key={title + columnIndex}
                                  className="px-6 py-2 text-lg font-medium text-gray-400 whitespace-nowrap"
                                >
                                  {Cell ? <Cell entry={entry} /> : entry[field]}
                                </td>
                              )
                            )}
                          </tr>
                        </>
                      ))}
                  </>
                ))}
            </tbody>
          </table>
        </div>
      ) : (
        <></>
      )}
      {showPagination && tableInitialState.tableTotalRecords > 1 && (
        <div className="pagination-outer">
          <ReactPaginate
            nextLabel="Next"
            onPageChange={handlePageClick}
            pageRangeDisplayed={3}
            marginPagesDisplayed={2}
            pageCount={tableInitialState.tableTotalRecords}
            previousLabel="Previous"
            pageClassName="page-item"
            pageLinkClassName="page-link"
            previousClassName="page-item"
            previousLinkClassName="page-link"
            nextClassName="page-item"
            nextLinkClassName="page-link"
            breakLabel="..."
            breakClassName="page-item"
            breakLinkClassName="page-link"
            containerClassName="pagination"
            activeClassName="active"
            forcePage={0}
          />
        </div>
      )}
      {!data || (data?.length == 0 && children ? children : <></>)}
    </div>
  );
};
